
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { Recipe } from '../types';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { triggerFavoriteAdd } from '@/utils/onboardingEvents';

const sampleRecipes: Recipe[] = [
  {
    id: '1',
    title: 'Ensalada de Quinoa',
    description: 'Una deliciosa ensalada vegana rica en proteínas',
    imageUrl: 'https://images.unsplash.com/photo-1618160702438-9b02ab6515c9',
    prepTime: 15,
    cookTime: 20,
    servings: 2,
    calories: 320,
    protein: 12,
    carbs: 45,
    fat: 9,
    nutriscore: 85,
    ingredients: [
      { id: '1', name: 'Quinoa', amount: 100, unit: 'g' },
      { id: '2', name: 'Tomate', amount: 1, unit: 'unidad' },
      { id: '3', name: 'Aguacate', amount: 1, unit: 'unidad' },
      { id: '4', name: 'Limón', amount: 1, unit: 'unidad' },
      { id: '5', name: 'Cilantro', amount: 1, unit: 'manojo' }
    ],
    instructions: [
      'Lava la quinoa y cocínala según las instrucciones del paquete.',
      'Corta el tomate y el aguacate en cubitos.',
      'Mezcla todos los ingredientes en un bowl.',
      'Exprime el limón por encima y añade el cilantro picado.',
      'Sazona con sal y pimienta al gusto.'
    ],
    tags: ['proteína', 'sin gluten', 'rápido'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '2',
    title: 'Curry de Garbanzos',
    description: 'Un curry reconfortante y nutritivo',
    imageUrl: 'https://images.unsplash.com/photo-1618160702438-9b02ab6515c9',
    prepTime: 10,
    cookTime: 25,
    servings: 4,
    calories: 380,
    protein: 15,
    carbs: 52,
    fat: 12,
    nutriscore: 78,
    ingredients: [
      { id: '6', name: 'Garbanzos cocidos', amount: 400, unit: 'g' },
      { id: '7', name: 'Leche de coco', amount: 400, unit: 'ml' },
      { id: '8', name: 'Cebolla', amount: 1, unit: 'unidad' },
      { id: '9', name: 'Curry en polvo', amount: 2, unit: 'cucharadas' },
      { id: '10', name: 'Tomate', amount: 2, unit: 'unidades' }
    ],
    instructions: [
      'Sofríe la cebolla picada hasta que esté translúcida.',
      'Añade el curry en polvo y cocina por 1 minuto más.',
      'Incorpora los tomates picados y cocina hasta que se ablanden.',
      'Agrega los garbanzos y la leche de coco. Deja cocinar a fuego lento por 15-20 minutos.',
      'Sirve con arroz o pan naan.'
    ],
    tags: ['reconfortante', 'proteína', 'legumbres'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

interface RecipesContextType {
  recipes: Recipe[];
  loading: boolean;
  addRecipe: (recipe: Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateRecipe: (id: string, updates: Partial<Recipe>) => Promise<void>;
  deleteRecipe: (id: string) => Promise<void>;
  getRecipe: (id: string) => Recipe | undefined;
  toggleFavorite: (id: string) => void;
  isFavorite: (id: string) => boolean;
}

const RecipesContext = createContext<RecipesContextType>({} as RecipesContextType);

const LOCAL_STORAGE_RECIPES_KEY = 'guia-vegana-recipes';
const LOCAL_STORAGE_FAVORITES_KEY = 'guia-vegana-favorites';

export const RecipesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, toggleFavorite: authToggleFavorite } = useAuth();

  useEffect(() => {
    console.log('[RecipesContext] Loading recipes from Supabase');
    console.trace();
    
    const loadRecipes = async () => {
      try {
        setLoading(true);
        
        const { data: supabaseRecipes, error } = await supabase
          .from('recipes')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error("Error loading recipes from Supabase:", error);
          setRecipes(sampleRecipes);
          return;
        }
        
        console.log("Loaded recipes from Supabase:", supabaseRecipes?.length);
        
        const formattedRecipes: Recipe[] = (supabaseRecipes || []).map(recipe => ({
          id: recipe.id,
          title: recipe.title,
          description: recipe.description || '',
          imageUrl: recipe.image_url || 'https://images.unsplash.com/photo-1618160702438-9b02ab6515c9',
          prepTime: recipe.prep_time || 0,
          cookTime: recipe.cook_time || 0,
          servings: recipe.servings || 1,
          calories: recipe.calories || 0,
          protein: recipe.protein || 0,
          carbs: recipe.carbs || 0,
          fat: recipe.fat || 0,
          iron: recipe.iron || 0,
          calcium: recipe.calcium || 0,
          zinc: recipe.zinc || 0,
          magnesium: recipe.magnesium || 0,
          omega3: recipe.omega3 || 0,
          nutriscore: recipe.nutriscore || 50,
          ingredients: recipe.ingredients || [],
          instructions: recipe.instructions || [],
          tags: recipe.tags || [],
          createdAt: recipe.created_at || new Date().toISOString(),
          updatedAt: recipe.updated_at || new Date().toISOString()
        }));
        
        setRecipes(formattedRecipes.length > 0 ? formattedRecipes : sampleRecipes);
      } catch (error) {
        console.error("Error loading recipes:", error);
        setRecipes(sampleRecipes);
      } finally {
        setLoading(false);
      }
    };

    loadRecipes();
  }, []);

  const addRecipe = async (recipeData: Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'>) => {
    console.log('[RecipesContext] addRecipe called');
    console.trace();
    
    try {
      const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      const now = new Date().toISOString();
      
      console.log("Adding recipe to Supabase:", recipeData.title);
      
      const validIngredients = recipeData.ingredients.map(ing => ({
        id: ing.id || `ing-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        name: ing.name || 'Sin nombre',
        amount: ing.amount !== undefined ? ing.amount : 0,
        unit: ing.unit || 'unidad'
      }));

      const validInstructions = Array.isArray(recipeData.instructions) 
        ? recipeData.instructions.map(String) 
        : [];
      
      const validTags = Array.isArray(recipeData.tags) 
        ? recipeData.tags.map(String)
        : [];
      
      const supabaseRecipe = {
        id: uniqueId,
        title: recipeData.title,
        description: recipeData.description,
        image_url: recipeData.imageUrl,
        prep_time: recipeData.prepTime,
        cook_time: recipeData.cookTime,
        servings: recipeData.servings,
        calories: recipeData.calories,
        protein: recipeData.protein,
        carbs: recipeData.carbs,
        fat: recipeData.fat,
        iron: recipeData.iron || 0,
        calcium: recipeData.calcium || 0,
        zinc: recipeData.zinc || 0,
        magnesium: recipeData.magnesium || 0,
        omega3: recipeData.omega3 || 0,
        nutriscore: recipeData.nutriscore,
        ingredients: validIngredients,
        instructions: validInstructions,
        tags: validTags,
        created_by: user?.id || null,
      };

      ['prep_time', 'cook_time', 'servings', 'calories', 'nutriscore'].forEach(field => {
        if (supabaseRecipe[field] !== undefined) {
          const parsedValue = parseInt(supabaseRecipe[field], 10);
          supabaseRecipe[field] = !isNaN(parsedValue) ? parsedValue : null;
        }
      });
      
      ['protein', 'carbs', 'fat', 'iron', 'calcium', 'zinc', 'magnesium', 'omega3'].forEach(field => {
        if (supabaseRecipe[field] !== undefined) {
          const parsedValue = parseFloat(supabaseRecipe[field]);
          supabaseRecipe[field] = !isNaN(parsedValue) ? parsedValue : null;
        }
      });

      console.log("Sending recipe to Supabase:", JSON.stringify(supabaseRecipe));

      const { error, data } = await supabase
        .from('recipes')
        .insert([supabaseRecipe])
        .select();

      if (error) {
        console.error("Supabase error when inserting recipe:", error);
        toast.error(`Error al añadir la receta: ${error.message || 'Error desconocido'}`);
        throw error;
      }

      const newRecipe: Recipe = {
        ...recipeData,
        ingredients: validIngredients,
        instructions: validInstructions,
        tags: validTags,
        id: uniqueId,
        createdAt: now,
        updatedAt: now
      };

      setRecipes(prev => [newRecipe, ...prev]);
      toast.success('Receta añadida correctamente');
      return Promise.resolve();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      console.error('Error adding recipe:', error);
      toast.error(`Error al añadir la receta: ${errorMessage}`);
      return Promise.reject(error);
    }
  };

  const updateRecipe = async (id: string, updates: Partial<Recipe>) => {
    console.log('[RecipesContext] updateRecipe called');
    console.trace();
    
    try {
      const supabaseUpdates: any = {};
      
      if (updates.title) supabaseUpdates.title = updates.title;
      if (updates.description !== undefined) supabaseUpdates.description = updates.description;
      if (updates.imageUrl) supabaseUpdates.image_url = updates.imageUrl;
      if (updates.prepTime !== undefined) supabaseUpdates.prep_time = updates.prepTime;
      if (updates.cookTime !== undefined) supabaseUpdates.cook_time = updates.cookTime;
      if (updates.servings !== undefined) supabaseUpdates.servings = updates.servings;
      if (updates.calories !== undefined) supabaseUpdates.calories = updates.calories;
      if (updates.protein !== undefined) supabaseUpdates.protein = updates.protein;
      if (updates.carbs !== undefined) supabaseUpdates.carbs = updates.carbs;
      if (updates.fat !== undefined) supabaseUpdates.fat = updates.fat;
      if (updates.iron !== undefined) supabaseUpdates.iron = updates.iron;
      if (updates.calcium !== undefined) supabaseUpdates.calcium = updates.calcium;
      if (updates.zinc !== undefined) supabaseUpdates.zinc = updates.zinc;
      if (updates.magnesium !== undefined) supabaseUpdates.magnesium = updates.magnesium;
      if (updates.omega3 !== undefined) supabaseUpdates.omega3 = updates.omega3;
      if (updates.nutriscore !== undefined) supabaseUpdates.nutriscore = updates.nutriscore;
      if (updates.ingredients) supabaseUpdates.ingredients = updates.ingredients;
      if (updates.instructions) supabaseUpdates.instructions = updates.instructions;
      if (updates.tags) supabaseUpdates.tags = updates.tags;
      
      supabaseUpdates.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from('recipes')
        .update(supabaseUpdates)
        .eq('id', id);

      if (error) throw error;

      const now = new Date().toISOString();
      setRecipes(prev => prev.map(recipe => 
        recipe.id === id ? { ...recipe, ...updates, updatedAt: now } : recipe
      ));
      
      toast.success('Receta actualizada correctamente');
      return Promise.resolve();
    } catch (error) {
      console.error("Error updating recipe:", error);
      toast.error('Error al actualizar la receta');
      return Promise.reject(error);
    }
  };

  const deleteRecipe = async (id: string) => {
    console.log('[RecipesContext] deleteRecipe called');
    console.trace();
    
    try {
      const { error } = await supabase
        .from('recipes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setRecipes(prev => prev.filter(recipe => recipe.id !== id));
      return Promise.resolve();
    } catch (error) {
      console.error('Error deleting recipe:', error);
      return Promise.reject(error);
    }
  };

  // Memoize getRecipe to prevent unnecessary re-renders
  const getRecipe = useCallback((id: string) => {
    console.log('[RecipesContext] getRecipe called for id:', id);
    console.trace();
    
    if (!id) {
      console.error("Recipe ID is undefined or empty");
      return undefined;
    }
    
    let recipe = recipes.find(r => r.id === id);
    
    if (!recipe) {
      const cleanId = id.replace(/^editar\//, '');
      
      recipe = recipes.find(r => 
        r.id === cleanId || 
        r.id === cleanId.replace(/^recipe-/, '') || 
        r.id === `recipe-${cleanId}` || 
        `recipe-${r.id}` === cleanId
      );
    }
    
    if (recipe) {
      const safeRecipe = {
        ...recipe,
        calories: typeof recipe.calories === 'number' && !isNaN(recipe.calories) ? recipe.calories : 0,
        protein: typeof recipe.protein === 'number' && !isNaN(recipe.protein) ? recipe.protein : 0,
        carbs: typeof recipe.carbs === 'number' && !isNaN(recipe.carbs) ? recipe.carbs : 0,
        fat: typeof recipe.fat === 'number' && !isNaN(recipe.fat) ? recipe.fat : 0,
        nutriscore: typeof recipe.nutriscore === 'number' && !isNaN(recipe.nutriscore) ? recipe.nutriscore : 0,
        servings: typeof recipe.servings === 'number' && !isNaN(recipe.servings) ? recipe.servings : 1,
        prepTime: typeof recipe.prepTime === 'number' && !isNaN(recipe.prepTime) ? recipe.prepTime : 0,
        cookTime: typeof recipe.cookTime === 'number' && !isNaN(recipe.cookTime) ? recipe.cookTime : 0,
      };
      
      console.log("Recipe found:", safeRecipe);
      return safeRecipe;
    } else {
      console.error("Recipe not found with ID:", id);
      console.log("Available recipe IDs:", recipes.map(r => r.id));
      return undefined;
    }
  }, [recipes]);

  const toggleFavorite = (id: string) => {
    console.log('[RecipesContext] toggleFavorite called');
    console.trace();
    
    if (!user) {
      toast.error("Debes iniciar sesión para guardar favoritos");
      return;
    }
    
    if (authToggleFavorite) {
      const isCurrentlyFavorite = user?.favorites?.includes(id) || false;
      
      if (!isCurrentlyFavorite) {
        triggerFavoriteAdd();
        console.log("Triggered favorite-added event from RecipesContext");
      }
      
      authToggleFavorite(id);
      
      const userId = user.id;
      let favorites = JSON.parse(localStorage.getItem(LOCAL_STORAGE_FAVORITES_KEY) || '{}');
      
      if (!favorites[userId]) {
        favorites[userId] = [];
      }
      
      const index = favorites[userId].indexOf(id);
      
      if (index === -1) {
        favorites[userId].push(id);
      } else {
        favorites[userId].splice(index, 1);
      }
      
      localStorage.setItem(LOCAL_STORAGE_FAVORITES_KEY, JSON.stringify(favorites));
    } else {
      if (!user.id) return;
      
      let favorites = JSON.parse(localStorage.getItem(LOCAL_STORAGE_FAVORITES_KEY) || '{}');
      
      if (!favorites[user.id]) {
        favorites[user.id] = [];
      }
      
      const index = favorites[user.id].indexOf(id);
      
      if (index === -1) {
        favorites[user.id].push(id);
        triggerFavoriteAdd();
        toast.success("Receta añadida a favoritos");
      } else {
        favorites[user.id].splice(index, 1);
        toast.success("Receta eliminada de favoritos");
      }
      
      localStorage.setItem(LOCAL_STORAGE_FAVORITES_KEY, JSON.stringify(favorites));
    }
  };

  const isFavorite = (id: string) => {
    if (!user) return false;
    
    if (user.favorites?.includes(id)) {
      return true;
    }
    
    try {
      const favorites = JSON.parse(localStorage.getItem(LOCAL_STORAGE_FAVORITES_KEY) || '{}');
      return Array.isArray(favorites[user.id]) && favorites[user.id].includes(id);
    } catch (e) {
      console.error("Error checking favorites in localStorage:", e);
      return false;
    }
  };

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      recipes,
      loading,
      addRecipe,
      updateRecipe,
      deleteRecipe,
      getRecipe,
      toggleFavorite,
      isFavorite
    }),
    [recipes, loading, getRecipe, toggleFavorite, isFavorite, addRecipe, updateRecipe, deleteRecipe]
  );

  return (
    <RecipesContext.Provider value={contextValue}>
      {children}
    </RecipesContext.Provider>
  );
};

export const useRecipes = () => useContext(RecipesContext);
